import { FC, PropsWithChildren } from 'react';

import cn from 'classnames';

import { utilityClasses } from '../../../css/utils';
import { Avatar } from '../../core/Avatars';
import { Popover } from '../../core/Popover';
import { H5, H6, H7 } from '../../core/Typography';

import styles from './ProfileMenu.module.css';
import {
  ActionsProps,
  ContentProps,
  ProfileProps,
  RootProps,
  TriggerProps,
} from './types';

/**
 * ProfileMenu component
 */
const Root: FC<PropsWithChildren<RootProps>> = ({
  children,
  openOnHover = true,
}) => (
  <Popover.Root closeDelay={100} openOnHover={openOnHover}>
    {children}
  </Popover.Root>
);

/**
 * Trigger component
 */
const Trigger: FC<PropsWithChildren<TriggerProps>> = ({
  children,
  dataTestId,
}) => (
  <Popover.Trigger asChild>
    <div className={utilityClasses['flex-row']} data-testid={dataTestId}>
      {children}
    </div>
  </Popover.Trigger>
);

/**
 * List of actions
 */
// TODO: Should be replaced with the Menu component ones it's implemented. https://app.clickup.com/t/86c11017q
const Actions: FC<ActionsProps> = ({ actions }) => (
  <div className={styles['menu-items-wrapper']}>
    {actions.map((action, index) => (
      <>
        <button
          key={`key-${action.component ? index : action.title}`}
          className={styles['menu-item']}
          data-disable-nprogress={true}
          onClick={action.onClick}
        >
          {action.component ?? (
            <>
              {action.icon}
              <H6 color="inherit" dataTestId={action.dataTestId}>
                {action.title}
              </H6>
            </>
          )}
        </button>
        {action?.appendDivider && (
          <div className={cn(utilityClasses.divider, styles['menu-divider'])} />
        )}
      </>
    ))}
  </div>
);

/**
 * Content wrapper component
 */
const Content: FC<PropsWithChildren<ContentProps>> = ({
  children,
  align,
  alignOffset,
  sideOffset,
}) => (
  <Popover.Content
    align={align}
    alignOffset={alignOffset}
    sideOffset={sideOffset}
  >
    <div className={styles.content}>
      <div className={utilityClasses['flex-column']}>{children}</div>
    </div>
  </Popover.Content>
);

/**
 * Profile component
 */
const Profile: FC<ProfileProps> = ({
  name,
  profileImage,
  dataTestId,
  email,
}) => (
  <>
    <div className={styles['profile-name-section']} data-testid={dataTestId}>
      <div className={styles['avatar-wrapper']}>
        <Avatar fluid fallback={name} src={profileImage || ''} />
      </div>
      <div className={styles['profile-name-info-wrapper']}>
        <H5 textOverflowEllipsis>
          <b>{name}</b>
        </H5>
        <H7 textOverflowEllipsis>{email}</H7>
      </div>
    </div>
  </>
);

export const ProfileMenu = {
  Root,
  Trigger,
  Content,
  Profile,
  Actions,
};
